/**
 * hitlQuestionKey — a stable, content-based identity for a HITL question.
 *
 * Two HITL panels (ora's WorkflowDetailDrawer and the Outlook add-in's
 * ReviewCheckpointPanel) need to know whether the question currently on
 * screen is still the SAME one the user just answered, so they can keep the
 * Approve/Reject buttons latched-disabled until a substantively DIFFERENT
 * question arrives. Object identity is unusable for this: the status poll
 * returns a fresh `hitl_question` object on every tick, so the reference
 * changes even when the underlying question is unchanged.
 *
 * Plain `JSON.stringify` is also unusable: the API's GetWorkflowStatusHandler
 * re-signs every screenshot/evidence URL with a fresh 15-min SAS token on
 * every fetch (api/Product.Application/Workflows/Queries/GetWorkflowStatus/
 * GetWorkflowStatusQuery.cs), so the serialized string would differ on every
 * poll even when the question is identical — releasing the latch mid-submit.
 *
 * This helper strips the query string from any http(s) URL value before
 * serializing, preserving the stable base URL (the question's real identity)
 * while discarding the volatile SAS token. The result is a string that is
 * equal across polls of the same question and differs when the substrate
 * advances to a new HITL gate.
 *
 * Canonical source for the add-in/ora parity rule (see ui/CLAUDE.md): both
 * surfaces MUST derive question identity the same way. Import this helper
 * rather than re-deriving it.
 */
export function hitlQuestionKey(hitlQuestion: unknown): string {
  if (hitlQuestion == null) return "null";
  return JSON.stringify(hitlQuestion, (_key, value) => {
    if (typeof value === "string" && /^https?:\/\/[^?]+\?/.test(value)) {
      return value.split("?")[0];
    }
    return value;
  });
}

export default hitlQuestionKey;
